import * as Sentry from '@sentry/react-native';
import { browserTracingIntegration } from '@sentry/browser';
import { Platform } from 'react-native';
import type { User } from '@/types/user';
import { getActingProviderId } from '@/store/actingProviderStore';
import { getApiBaseUrl } from '@/lib/api-url';

const isWeb = Platform.OS === 'web';

/**
 * Registrado no container do expo-router (`useNavigationContainerRef`) no
 * layout raiz — precisa ser a mesma instância usada em `initSentry` e no
 * `registerNavigationContainer`.
 */
export const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: true,
});

/** Acesso estático — Metro só embute EXPO_PUBLIC_* assim (não usar process.env[key]). */
const SENTRY_DSN = process.env.EXPO_PUBLIC_SENTRY_DSN;

/**
 * SHA do commit — mesmo valor usado no `action-release`/upload de source maps
 * dos workflows (mobile e web). Sem isto, a release da web cai no nome/versão
 * fixos do `app.json` (não muda por deploy) e o "suspect commit" do Sentry
 * nunca teria uma release nova para apontar.
 */
const SENTRY_RELEASE = process.env.EXPO_PUBLIC_SENTRY_RELEASE;
const SENTRY_ENVIRONMENT = process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT;

/**
 * Só o domínio da própria API — nunca Stripe, CEP ou geocode (serviços de
 * terceiros que o app chama direto). Sem isto, o SDK usa um catch-all por
 * padrão no mobile e vaza `sentry-trace`/`baggage` pra qualquer host.
 */
function getTracePropagationTargets(): string[] {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) return [];
  try {
    return [new URL(apiBaseUrl).host];
  } catch {
    return [];
  }
}

/** Sem DSN (dev local, PR preview sem secret), o SDK simplesmente não liga. */
export function initSentry() {
  if (!SENTRY_DSN) return;

  Sentry.init({
    dsn: SENTRY_DSN,
    release: SENTRY_RELEASE,
    environment: SENTRY_ENVIRONMENT,
    sendDefaultPii: false,
    // Web usa o tracing/replay do navegador (Web Vitals inclusos no
    // `browserTracingIntegration`, que o `reactNativeTracingIntegration`
    // padrão não coleta); por isso ele fica desligado só na web, pra não
    // duplicar spans de fetch/xhr. Mobile continua com o padrão do SDK.
    enableAutoPerformanceTracing: !isWeb,
    integrations: isWeb
      ? [
          navigationIntegration,
          browserTracingIntegration(),
          Sentry.browserReplayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            block: ['iframe'], // Stripe Elements
          }),
        ]
      : [navigationIntegration],
    tracesSampleRate: 1.0,
    tracePropagationTargets: getTracePropagationTargets(),
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: isWeb ? 0.05 : 0,
  });
}

/** `setUser`/tags no login, limpos no logout — chamado pelo authStore. */
export function syncSentryUser(user: User | null) {
  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('role', undefined);
    Sentry.setTag('acting_provider', undefined);
    Sentry.setTag('organization.id', undefined);
    return;
  }

  Sentry.setUser({ id: user.id });
  Sentry.setTag('role', user.role);
  Sentry.setTag('acting_provider', getActingProviderId() ?? undefined);
  Sentry.setTag('organization.id', user.organizationId ?? undefined);
}

/** Chamado pelo error boundary — anota a rota em que o erro aconteceu. */
export function captureBoundaryError(error: Error, route: string | null) {
  Sentry.captureException(error, { tags: { route: route ?? undefined } });
}
